import React, { useState } from 'react';
import { API_CONFIG } from '../../config/api';

interface AddNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

const AddNewsModal: React.FC<AddNewsModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    mainTopic: '',
    additionalTopicsEn: '',
    additionalTopicsDe: '',
    link: '',
    language: '',
  });
  const [image, setImage] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImage(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('summary', formData.summary);
      formDataToSend.append('mainTopic', formData.mainTopic);
      formDataToSend.append('additionalTopicsEn', formData.additionalTopicsEn);
      formDataToSend.append('additionalTopicsDe', formData.additionalTopicsDe);
      formDataToSend.append('link', formData.link);
      formDataToSend.append('language', formData.language);
      if (image) {
        formDataToSend.append('image', image);
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.MANUAL}`, {
        method: 'POST',
        body: formDataToSend,
      });

      if (!response.ok) {
        throw new Error('Failed to add news');
      }

      onSave();
      onClose();
    } catch (err) {
      setError('Failed to add news. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add New News</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="summary">Summary</label>
            <textarea
              id="summary"
              name="summary"
              value={formData.summary}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="mainTopic">Main Topic</label>
            <input
              type="text"
              id="mainTopic"
              name="mainTopic"
              value={formData.mainTopic}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="additionalTopicsEn">Additional Topics (English)</label>
            <input
              type="text"
              id="additionalTopicsEn"
              name="additionalTopicsEn"
              value={formData.additionalTopicsEn}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="additionalTopicsDe">Additional Topics (German)</label>
            <input
              type="text"
              id="additionalTopicsDe"
              name="additionalTopicsDe"
              value={formData.additionalTopicsDe}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="link">Article Link</label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="language">Language</label>
            <select
              id="language"
              name="language"
              value={formData.language}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Language</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="it">Italian</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="image">Image</label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
              required
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-footer">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add News'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNewsModal;